import { useEffect, useState } from 'react';
import { capitalize } from 'lodash-es';

export function useSyncElementDimension<
    Source extends HTMLElement,
    Target extends HTMLElement
>({
    dimension,
    sourceEl,
    targetEl,
    strategy = 'source-to-target'
}: {
    dimension: 'width' | 'height';
    sourceEl?: Source;
    targetEl?: Target;
    strategy?: 'source-to-target' | 'max';
}) {
    const [internalSourceEl, setInternalSourceEl] = useState<Source | null>(
        null
    );

    const [internalTargetEl, setInternalTargetEl] = useState<Target | null>(
        null
    );

    const _sourceEl = sourceEl || internalSourceEl;
    const _targetEl = targetEl || internalTargetEl;

    useEffect(() => {
        if (!_sourceEl || !_targetEl) {
            return;
        }
        const capitalized: 'Width' | 'Height' = capitalize(dimension) as
            | 'Width'
            | 'Height';
        const min = `min${capitalized}` as const;
        const max = `max${capitalized}` as const;
        const isMaxStrategy = strategy === 'max';

        function syncDimension() {
            // reset styles so that the elements return to their natural size before syncing
            _sourceEl.style[min] =
                _sourceEl.style[max] =
                _targetEl.style[min] =
                _targetEl.style[max] =
                    null;

            const sourceValue = getComputedStyle(_sourceEl)[dimension];

            let elementToModify: HTMLElement;
            let referenceValue: string;

            if (strategy === 'source-to-target') {
                elementToModify = _targetEl;
                referenceValue = sourceValue;
            } else if (isMaxStrategy) {
                const targetValue = getComputedStyle(_targetEl)[dimension];

                if (parseFloat(sourceValue) >= parseFloat(targetValue)) {
                    elementToModify = _targetEl;
                    referenceValue = sourceValue;
                } else {
                    elementToModify = _sourceEl;
                    referenceValue = targetValue;
                }
            }

            elementToModify.style[min] = elementToModify.style[max] =
                referenceValue;
        }

        syncDimension();

        const resizeObserver = new ResizeObserver(syncDimension);
        resizeObserver.observe(_sourceEl);

        let mutationObserver: MutationObserver;
        if (isMaxStrategy) {
            resizeObserver.observe(_targetEl);

            mutationObserver = new MutationObserver(syncDimension);
            const mutationObserverOptions: MutationObserverInit = {
                subtree: true,
                childList: true,
                characterData: true
            };
            mutationObserver.observe(_sourceEl, mutationObserverOptions);
            mutationObserver.observe(_targetEl, mutationObserverOptions);
        }

        return () => {
            resizeObserver.disconnect();
            mutationObserver?.disconnect();
        };
    }, [_sourceEl, _targetEl, dimension, strategy]);

    return {
        sourceEl: _sourceEl,
        targetEl: _targetEl,
        setTargetEl: setInternalTargetEl,
        setSourceEl: setInternalSourceEl
    };
}
